import { useEffect, useState } from "react";
import { Link } from "wouter";
import { AdvicelineNurseLayout } from "@/components/layout/adviceline-nurse-layout";
import { NurseLayout } from "@/components/layout/nurse-layout";
import { 
  User, Building2, MapPin, BadgeCheck, Mail, Hash, Shield, 
  Loader2, Pencil,
} from "lucide-react";

interface ProfileData {
  hasProfile?: boolean;
  userId?: string;
  role?: string | null;
  approvalStatus?: string | null;
  organisationName?: string | null;
  siteName?: string | null;
  teamCode?: string | null;
  jobRole?: string | null;
  staffCode?: string | null;
  workEmail?: string | null;
}

export default function NurseSettings() {
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/user/profile-summary", { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(data => { setProfile(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const isAdvicelineHub = typeof window !== "undefined" && window.location.hostname.includes("advicelinehub");
  const Layout = isAdvicelineHub ? AdvicelineNurseLayout : NurseLayout;

  if (loading) {
    return (
      <Layout title="Settings">
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  const items = [
    { icon: User, label: "Name", value: profile?.userId ? "Signed in" : "—" },
    { icon: Hash, label: "Staff Code", value: profile?.staffCode ?? "Not yet assigned", highlight: true },
    { icon: BadgeCheck, label: "Role", value: profile?.role ? profile.role.charAt(0).toUpperCase() + profile.role.slice(1) : "—" },
    { icon: Building2, label: "Organisation", value: profile?.organisationName ?? "—" },
    { icon: MapPin, label: "Location", value: profile?.siteName ?? "—" },
    { icon: BadgeCheck, label: "Specialty / Job Role", value: profile?.jobRole?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) ?? "—" },
    { icon: Mail, label: "Shared Advice Line Email", value: profile?.workEmail ?? "—" },
    { icon: Shield, label: "Approval Status", value: profile?.approvalStatus === "approved" ? "Approved" : profile?.approvalStatus ?? "—" },
  ];

  return (
    <Layout title="Settings">
      <div className="max-w-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Account Details</h2>
          <Link href={isAdvicelineHub ? "/nurse/profile" : "/nurse/profile"} className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-600 hover:text-teal-800 transition-colors bg-teal-50 hover:bg-teal-100 rounded-full px-4 py-1.5">
            <Pencil className="h-3.5 w-3.5" />
            Edit Profile
          </Link>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100 overflow-hidden">
          {items.map((item, i) => (
            <div key={i} className="flex items-center gap-4 px-5 py-4">
              <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                <item.icon className="h-4 w-4 text-slate-500" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-slate-400">{item.label}</p>
                <p className={`text-sm ${item.highlight ? "font-mono font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded inline-block mt-0.5" : "font-medium text-slate-800 truncate"}`}>
                  {item.value}
                </p>
              </div>
            </div>
          ))}
        </div>

        <p className="text-xs text-slate-400 mt-4 text-center">
          Your staff code is used to sign off advice line requests. Keep it confidential.
        </p>
      </div>
    </Layout>
  );
}
